import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import * as XLSX from "xlsx";
import styles from "./BulInviteStyle.module.scss";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2,
  Stack,
} from "@mui/material";
import { ReactComponent as InviteBluk } from "../../../../AdminAssets/svg/InviteBulk.svg";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { getRoleInOrg } from "../../../../../../Utils/organizations";
import { getDisplayRoleId } from "../../../../../../Utils/Roles";
import Popup from "../../../../../../GlobalComponents/Popup/Popup";
import { useDropzone } from "react-dropzone";
import { formatFileSize, useEmailId } from "../../../../../../Utils/utils";

const BulkInvite = ({
  orgInfo,
  setCreateOrgFormBulk,
  bulkData,
  setBulkData,
  setBulkFile,
  bulkFileName,
  setBulkFileName,
  bulkFileSize,
  setBulkFileSize,
  members,
  redundantEmails,
  setRedundantEmails,
  handleRemoveAllClick,
  errors,
  setErrors,
}) => {
  const bulkUserLimit = process.env.REACT_APP_BULK_USER_IMPORT_EMAILS;
  const [largeFileError, setLargeFileError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [fileError, setFileError] = useState("");
  const [sheetError, setSheetError] = useState("");
  const [isInviteDisabled, setIsInviteDisabled] = useState(true);
  const [ownerError, setOwnerError] = useState("");
  const [showRedundantEmailWarning, setShowRedundantEmailWarning] =
    useState(false);
  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupButtonFunction: () => {},
  });

  const fileInputRef = useRef(null);
  const emailId = useEmailId();
  const teamMembers = orgInfo?.members || [];
  const emailIdArray = teamMembers?.map((member) => member.emailId);

  const checkIfOrgMemberExistsInArray = useCallback(() => {
    let res = false;
    bulkData?.forEach((item) => {
      const email = item.email;
      if (emailIdArray?.includes(email)) {
        res = true;
      }
    });
    return res;
  }, [bulkData, emailIdArray]);

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const isEmailIdInBulk = bulkData?.some((item) => item.email === emailId);

  const checkEmailsAndSetWarnings = useCallback(() => {
    let disableInvite = false;
    const orgMembers = members?.map((member) => member.emailId);
    const emailCounts = {};
    const commonEmails = [];
    let hasInvalidEmail = false;
    let hasInvalidEmailCount = 0;
    const updatedRedundantEmails = [];

    if (bulkData?.length > 0) {
      bulkData.forEach((item) => {
        const email = item.email;
        if (!isValidEmail(email)) {
          hasInvalidEmail = true;
          hasInvalidEmailCount++;
        }
        emailCounts[email] = (emailCounts[email] || 0) + 1;
        if (emailCounts[email] === 2) {
          commonEmails.push(email);
        }
        if (orgMembers?.includes(email)) {
          updatedRedundantEmails.push(email);
        }

        if (isEmailIdInBulk) {
          setOwnerError("Logged in user email id is in the list.");
        }
      });

      const uniqueRedundantEmails = [...new Set(updatedRedundantEmails)];

      if (
        hasInvalidEmail ||
        commonEmails?.length > 0 ||
        checkIfOrgMemberExistsInArray()
      ) {
        disableInvite = true;
      }

      const newErrorMessage = hasInvalidEmail
        ? `Error Email id's (${hasInvalidEmailCount})`
        : "";
      if (errorMessage !== newErrorMessage) {
        setErrorMessage(newErrorMessage);
      }
      const hasCommonEmails = commonEmails?.length > 0;
      if (
        JSON.stringify(redundantEmails) !==
          JSON.stringify(uniqueRedundantEmails) ||
        showRedundantEmailWarning !== hasCommonEmails
      ) {
        const combinedEmails = [
          ...new Set([...uniqueRedundantEmails, ...commonEmails]),
        ];
        if (
          JSON.stringify(redundantEmails) !== JSON.stringify(combinedEmails)
        ) {
          setRedundantEmails(combinedEmails);
        }

        setShowRedundantEmailWarning(hasCommonEmails);
      }
    }
    if (isInviteDisabled !== disableInvite) {
      setIsInviteDisabled(disableInvite);
    }
  }, [
    bulkData,
    errorMessage,
    ownerError,
    isInviteDisabled,
    members,
    showRedundantEmailWarning,
    checkIfOrgMemberExistsInArray,
  ]);

  const checkInvalidRoles = useCallback(() => {
    const validRoles = ["super admin", "admin", "user"];
    let invalidRoleFound = false;
    const updatedData = bulkData?.map((item) => {
      const trimmedRole = item.userRole?.trim().toLowerCase();
      const isInvalidRole = !validRoles?.includes(trimmedRole);
      if (isInvalidRole) {
        invalidRoleFound = true;
        setErrorMessage("InValid roles detected");
      } else setErrorMessage("");
      return {
        ...item,
        isInvalidRole,
      };
    });

    setBulkData((prevData) => {
      if (JSON.stringify(prevData) !== JSON.stringify(updatedData)) {
        return updatedData;
      }
      return prevData;
    });

    setIsInviteDisabled((prev) => {
      if (prev !== invalidRoleFound) {
        return invalidRoleFound;
      }
      return prev;
    });
  }, [bulkData]);

  useEffect(() => {
    checkEmailsAndSetWarnings();
    checkInvalidRoles();
  }, [checkEmailsAndSetWarnings, checkInvalidRoles]);

  useEffect(() => {
    const hasErrors =
      errors?.length > 0 ||
      redundantEmails?.length > 0 ||
      checkIfOrgMemberExistsInArray();
    setIsInviteDisabled(
      hasErrors || bulkData?.some((item) => item.isInvalidRole)
    );
  }, [bulkData, errors, redundantEmails, checkIfOrgMemberExistsInArray]);

  useEffect(() => {
    const handleHardRefresh = () => {
      setBulkData([]);
      setBulkFile(null);
      setBulkFileName("");
      setBulkFileSize("");
      setErrors([]);
      setRedundantEmails([]);
    };

    window.addEventListener("beforeunload", handleHardRefresh);

    return () => {
      window.removeEventListener("beforeunload", handleHardRefresh);
    };
  }, []);

  const userRoleInOrg = getRoleInOrg(emailId, orgInfo);
  const rolesValid = ["super admin", "admin", "user"];
  const validRoles = useMemo(() => {
    return userRoleInOrg === "Admin" ? ["admin", "user"] : [];
  }, [userRoleInOrg]);

  const handleUpload = useCallback(
    (selectedFile) => {
      const maxSizeInBytes = 5 * 1024 * 1024;
      if (selectedFile.size > maxSizeInBytes) {
        setLargeFileError(
          "File is too large. Please upload a file smaller than 5MB."
        );
        return;
      } else setLargeFileError("");

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e?.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        if (workbook.SheetNames?.length > 1) {
          setSheetError("Multiple sheets detected. Please upload a valid file");
          return;
        } else setSheetError("");

        if (
          !sheet["A1"] ||
          !sheet["B1"] ||
          sheet["A1"].v.toLowerCase() !== "email id" ||
          sheet["B1"].v.toLowerCase() !== "role"
        ) {
          setFileError("Invalid File Format! Please upload a valid file.");
          return;
        }
        setFileError("");

        if (userRoleInOrg === "Admin") {
          // Check for Super Admin roles in the file
          for (let i = 2; i <= bulkUserLimit; i++) {
            const userRole = sheet[`B${i}`]?.v?.toLowerCase().trim();
            if (userRole === "super admin") {
              setFileError("As admin you can invite only admins and users");
              return;
            }
          }
        }

        const extractedData = [];
        const errorEmails = [];

        for (let i = 2; i <= bulkUserLimit; i++) {
          const email = sheet[`A${i}`]?.v;
          const userRole = sheet[`B${i}`]?.v?.toLowerCase();

          if (email && userRole) {
            if (isValidEmail(email)) {
              const normalizedUserRole = userRole.trim().toLowerCase();
              if (!validRoles?.includes(normalizedUserRole)) {
                extractedData.push({ email, userRole });
              }
            } else {
              extractedData.push({
                email: <span className={styles.errorMail}>{email}</span>,
                userRole,
              });
              errorEmails.push(email);
              setIsInviteDisabled(true);
            }
            if (errorEmails?.length > 0) {
              setErrors(errorEmails);
              setIsInviteDisabled(true);
            } else {
              setErrors([]);
            }
          }
        }

        const res = extractedData?.map((item) => {
          const getRole = getDisplayRoleId(item.userRole);
          return {
            memberEmailId: item.email,
            roleId: getRole,
          };
        });

        setCreateOrgFormBulk(res);
        setBulkData(extractedData);
        setErrors(errorEmails);
        setBulkFile(selectedFile);
        setBulkFileName(selectedFile.name);
        setBulkFileSize(formatFileSize(selectedFile.size));
      };
      reader.readAsArrayBuffer(selectedFile);

      fileInputRef.current.value = "";
    },
    [
      setPopup,
      setCreateOrgFormBulk,
      setErrors,
      setBulkFile,
      setBulkFileName,
      validRoles,
      bulkUserLimit,
      fileInputRef,
      userRoleInOrg,
    ]
  );

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length > 0) {
        setErrorMessage(
          "Unsupported file format. Please upload an Excel or CSV file."
        );
        return;
      } else setErrorMessage("");

      const selectedFile = acceptedFiles[0];
      setBulkFile(selectedFile);
      setBulkFileName(selectedFile.name);
      setBulkFileSize(formatFileSize(selectedFile.size));
      handleUpload(selectedFile);
    },
    [handleUpload]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    onDrop,
    noClick: false,
  });

  const handleRemoveClick = (index) => {
    const updatedData = [...bulkData];
    updatedData.splice(index, 1);
    setBulkData(updatedData);

    const remainingErrors = updatedData.filter(
      (item) => !isValidEmail(item.email)
    );
    setErrors(remainingErrors?.map((item) => item.email));

    const updatedBulkData = updatedData?.map((item) => {
      const getRole = getDisplayRoleId(item.userRole);
      return {
        memberEmailId: item.email,
        roleId: getRole,
      };
    });
    setCreateOrgFormBulk(updatedBulkData);

    if (!updatedData || updatedData?.length === 0) {
      setBulkFile(null);
      setIsInviteDisabled(remainingErrors?.length > 0);
    }
  };

  return (
    <Stack className={styles.bulkUserContainer}>
      <div
        style={{ display: "flex", flexDirection: "column" }}
        {...getRootProps()}
      >
        {!bulkFileName && (
          <div className={styles.imageContainer}>
            <InviteBluk className={styles.imageColor} />
          </div>
        )}
        {!bulkFileName ? (
          <div className={styles.textContainer}>
            <div
              className={styles.uploadtext}
              onClick={() => fileInputRef.current.click()}
            >
              <span className={styles.uploadLabel}>
                Drag and drop or{" "}
                <span className={styles.highlight}>choose file</span> to upload
              </span>
              <span className={styles.maxSizeLabel}>Maximum size: 5MB</span>
              {errorMessage && (
                <div className={styles.errorMail}>
                  File is too large. Please upload a file smaller than 5MB
                </div>
              )}
              {fileError && <div className={styles.errorMail}>{fileError}</div>}
              {sheetError && (
                <div className={styles.errorMail}>{sheetError}</div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.table}></table>
            </div>
            <div className={styles.tableContent}>
              <table className={styles.tablePadding}>
                <tbody>
                  {bulkData?.length > 0 && (
                    <TableContainer>
                      <Table>
                        <TableHead className={styles.tableHead}>
                          <TableRow>
                            <TableCell className={styles.tableRowCell}>
                              S.No
                            </TableCell>
                            <TableCell className={styles.tableRowCell}>
                              Email Address
                            </TableCell>
                            <TableCell className={styles.tableRowCell}>
                              Role
                            </TableCell>
                            <TableCell className={styles.tableRowCell}>
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {bulkData?.map((item, index) => {
                            const isLoggedInMail = item.email === emailId;
                            const emailError = !isValidEmail(item.email);
                            const roleError = !rolesValid?.includes(
                              item.userRole
                            );
                            const isRedundantEmail = redundantEmails?.includes(
                              item.email
                            );
                            const hasError = emailError || roleError;

                            return (
                              <TableRow key={index}>
                                <TableCell className={styles.tableBodyText}>
                                  <span
                                    className={`${
                                      hasError && styles.errorMail
                                    } ${
                                      isLoggedInMail && styles.loggedInMail
                                    } ${
                                      isRedundantEmail && styles.warningMail
                                    }`}
                                  >
                                    {index + 1}
                                  </span>
                                </TableCell>
                                <TableCell className={styles.tableBodyText}>
                                  <span
                                    className={`${
                                      hasError && styles.errorMail
                                    } ${
                                      isLoggedInMail && styles.loggedInMail
                                    } ${
                                      isRedundantEmail && styles.warningMail
                                    }`}
                                  >
                                    {item.email}
                                  </span>
                                </TableCell>
                                <TableCell className={styles.tableBodyText}>
                                  <span
                                    className={`${
                                      hasError && styles.errorMail
                                    } ${
                                      isLoggedInMail && styles.loggedInMail
                                    } ${
                                      isRedundantEmail && styles.warningMail
                                    }`}
                                  >
                                    {item.userRole}
                                  </span>
                                </TableCell>
                                <TableCell className={styles.delete}>
                                  <DeleteOutlinedIcon
                                    onClick={() => handleRemoveClick(index)}
                                    className={styles.deleteIcon}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <input
        {...getInputProps()}
        id="file-upload"
        style={{ display: "none" }}
        type="file"
        onChange={(e) => {
          const selectedFile = e?.target.files[0];
          const handleFileChange = (e) => {
            const selectedFile = e?.target.files[0];
            if (selectedFile) {
              setBulkFile(selectedFile);
            }
          };
          if (selectedFile) {
            handleUpload(selectedFile);
            handleFileChange();
          }
        }}
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
        ref={fileInputRef}
      />
      <Grid2 className={styles.errorAndBtnContainer}>
        <Grid2 className={styles.infoText}>
          {bulkFileName && (
            <div>
              {bulkData?.length}/200 candidates are added
              {redundantEmails?.length > 0 && (
                <>
                  <Grid2 container alignItems={"center"}>
                    <div className={styles.warningText} />
                    <div>
                      {redundantEmails?.length === 1
                        ? `Redundant email found (${redundantEmails?.length})`
                        : `Redundant emails found: (${redundantEmails?.length})`}
                    </div>
                  </Grid2>
                </>
              )}
              {isEmailIdInBulk ? (
                <>
                  <Grid2 container alignItems={"center"}>
                    <div className={styles.loggedInText} />
                    <div>{ownerError}</div>
                  </Grid2>
                </>
              ) : (
                ""
              )}
              {largeFileError !== "" && (
                <>
                  <Grid2 container alignItems={"center"}>
                    <div className={styles.errorText} />
                    <div>{largeFileError}</div>
                  </Grid2>
                </>
              )}
              {errorMessage !== "" && (
                <>
                  <Grid2 container alignItems={"center"}>
                    <div className={styles.errorText} />
                    <div>{errorMessage}</div>
                  </Grid2>
                </>
              )}
            </div>
          )}
        </Grid2>
        {bulkFileName ? (
          <Grid2 className={styles.fileNameContainer}>
            <Grid2 className={styles.fileNameSize}>
              <Grid2>{bulkFileName}</Grid2>
              <Grid2 className={styles.fileSize}>{bulkFileSize}</Grid2>
            </Grid2>
            <Grid2 onClick={handleRemoveAllClick}>
              <DeleteOutlinedIcon />
            </Grid2>
          </Grid2>
        ) : (
          ""
        )}
      </Grid2>
      <Popup
        trigger={popup.popupState}
        type={popup.popupType}
        title={popup.popupTitle}
        subtitle={popup.popupSubtitle}
        button={popup.popupButton}
        cancel={popup.popupCancel}
        buttonTitle={popup.popupButtonTitle}
        buttonFunction={popup.popupButtonFunction}
      />
    </Stack>
  );
};

export default BulkInvite;
